import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../api';
import { formatCurrency as formatCurrencyUtil, getCurrencySymbol as getSymbol } from '../utils/currencyFormatter';

export const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [symbol, setSymbol] = useState('$');
  const [currency, setCurrency] = useState('CAD');
  const [isLoading, setIsLoading] = useState(true);

  // Format currency with user's currency
  const formatCurrency = useCallback((amount, options = {}) => {
    return formatCurrencyUtil(amount, currency, options);
  }, [currency]);

  // Format currency for display (short format)
  const formatAmount = useCallback((amount) => {
    return formatCurrencyUtil(amount, currency, { decimals: 2, includeCode: false });
  }, [currency]);

  // Format currency with code (e.g., "$100.00 CAD")
  const formatAmountWithCode = useCallback((amount) => {
    return formatCurrencyUtil(amount, currency, { decimals: 2, includeCode: true });
  }, [currency]);

  useEffect(() => {
    const fetchUserCurrency = () => {
      const token = localStorage.getItem("token");
      const userId = token ? JSON.parse(atob(token.split('.')[1]))?.userId : null;
      
      if (userId) {
        api.get(`/users/${userId}`, { headers: { Authorization: token } })
          .then(res => {
            if (res.data.currencyCode) {
              const code = res.data.currencyCode.toUpperCase();
              setCurrency(code);
              setSymbol(getSymbol(code));
            }
            setIsLoading(false);
          })
          .catch(err => {
            console.error('Error fetching user currency:', err);
            setIsLoading(false);
          });
      } else {
        setIsLoading(false);
      }
    };

    // Fetch immediately
    fetchUserCurrency();

    // Listen for storage changes (when user logs in)
    const handleStorageChange = (e) => {
      if (e.key === 'token' && e.newValue) {
        setIsLoading(true);
        fetchUserCurrency();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom login event
    const handleLogin = () => {
      setIsLoading(true);
      fetchUserCurrency();
    };
    
    window.addEventListener('user-logged-in', handleLogin);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-logged-in', handleLogin);
    };
  }, []);

  return (
    <CurrencyContext.Provider value={{ 
      symbol, 
      setSymbol, 
      currency, 
      setCurrency,
      formatCurrency,
      formatAmount,
      formatAmountWithCode,
      isLoading
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};
